import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Chip
} from "@mui/material";
import { ArrowBack, Receipt, Edit, CloudUpload, LocalShipping, CheckCircle, SupportAgent, Warning, RadioButtonUnchecked, Add } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { OrderData } from "../../../components/OrderCard";

// Visuals
const BG_IMAGE = "https://media.rainpos.com/663/hex5f021f_burgundy_design_swirl_boldtiny09867633333333.jpg";

// Status Mapping to Steps
const STEPS = ["Order Placed", "Info Received", "Design in Progress", "Review", "Shipped/Delivered"];

const getActiveStep = (status: string) => {
  switch (status) {
    case 'pending': return 0;
    case 'awaiting-info': return 1;
    case 'in-progress': return 2;
    case 'ready-for-download': return 3;
    case 'shipped':
    case 'delivered':
    case 'completed': return 5;
    default: return 0;
  }
};

const OrderDetailsStudio: React.FC<{ manualOrder?: OrderData; onNavigate?: (path: string) => void }> = ({ manualOrder, onNavigate }) => {
  const { orderId } = useParams<{ orderId: string }>();
  const routerNavigate = useNavigate();
  const [order, setOrder] = useState<OrderData | null>(manualOrder || null);
  const [loading, setLoading] = useState(!manualOrder);

  const safeNavigate = (path: string) => {
      if (onNavigate) {
          onNavigate(path);
      } else {
          routerNavigate(path);
      }
  };

  useEffect(() => {
    if (manualOrder) {
        setOrder(manualOrder);
        setLoading(false);
        return;
    }
    const fetchOrder = async () => {
      if (!orderId) return;
      try {
        const docRef = doc(db, "orders", orderId);
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          setOrder({ paypalOrderId: snapshot.id, ...snapshot.data() } as OrderData);
        }
      } catch (err) {
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!order) return <Box sx={{ p: 4 }}>Order not found.</Box>;

  // Derived State
  const isMissionary = order?.items?.some((i: any) => 
    i.productId === 'mpa-art' || i.name?.includes('Missionary')
  );
  const isFamilyTree = order.items.some(i => i.name.toLowerCase().includes('tree') || i.productId?.toLowerCase().includes('pft'));
  const isGenerational = order.items.some(i => i.productId?.includes('ga-') || i.name?.includes('Generational'));

  const isLinked = !!order?.linkedMissionaryId;
  const linkedName = order?.linkedMissionaryName || "Missionary";
  
  const isLocked = order ? (order.status !== 'pending' && order.status !== 'awaiting-info') : false;

  const isInfoComplete = isMissionary 
      ? !!order.formStatus?.Missionary 
      : isFamilyTree 
          ? (order.generationProgress?.completedGenerations || 0) >= (order.generationProgress?.totalGenerations || 1)
          : isGenerational
            ? !!order.formStatus?.Generational
          : true;
  
  const isPhotosComplete = (order.imageProgress?.uploadedImages || 0) > 0;

  const showChecklist = order.status === 'pending' || order.status === 'awaiting-info';

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", display: 'flex', flexDirection: 'column' }}>
       {/* Small Header */}
       <Box sx={{ 
        height: "100px", 
        backgroundImage: `url('${BG_IMAGE}')`, 
        backgroundSize: "cover",
        display: "flex", alignItems: "center"
      }}>
        <Container maxWidth="lg">
           <Button 
            startIcon={<ArrowBack />} 
            sx={{ color: "white", textTransform: 'none' }}
            onClick={() => safeNavigate('/account')}
           >
            Back to Studio
           </Button>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -4, mb: 8, flexGrow: 1 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
           {/* Header Info */}
           <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
             <Box>
                <Typography variant="overline" color="text.secondary">Order #{order.paypalOrderId}</Typography>
                <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif' }}>
                  {order.items[0]?.name || "Custom Art Project"}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                   Placed on {order.createdAt?.toDate?.().toLocaleDateString() || new Date().toLocaleDateString()}
                </Typography>
             </Box>
             <Box sx={{ textAlign: 'right' }}>
               <Chip label={order.status.toUpperCase().replace('-', ' ')} color="primary" sx={{ fontWeight: 'bold' }} />
               <Typography variant="h6" sx={{ mt: 1 }}>
                 {order.amount} {order.currency}
               </Typography>
             </Box>
           </Box>

           <Divider sx={{ mb: 4 }} />

           {/* Timeline */}
           <Box sx={{ mb: 6 }}>
             <Stepper activeStep={getActiveStep(order.status)} alternativeLabel>
               {STEPS.map((label) => (
                 <Step key={label}>
                   <StepLabel>{label}</StepLabel>
                 </Step>
               ))}
             </Stepper>
           </Box>



           {/* Action Checklist */}
           {showChecklist && (
               <Paper elevation={0} sx={{ mb: 6, p: 3, bgcolor: '#fff4e5', border: '1px solid #ffe0b2', borderRadius: 2 }}>
                   <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#e65100', display: 'flex', alignItems: 'center', gap: 1 }}>
                       <Warning color="warning" /> Action Required
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 2 }}>
                       Please complete the following items so we can start your design:
                   </Typography>
                   
                   <Grid container spacing={2}>
                       {/* Info Step */}
                       {(isMissionary || isFamilyTree || isGenerational) && (
                           <Grid size={{ xs: 12, sm: 6 }}>
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'white', borderRadius: 1, border: isInfoComplete ? '1px solid #e0e0e0' : '1px solid #ffcc80' }}>
                                   {isInfoComplete ? (
                                       <CheckCircle color="success" />
                                   ) : (
                                       <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />
                                   )}
                                   <Box>
                                       <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: isInfoComplete ? 'text.secondary' : 'text.primary', textDecoration: isInfoComplete ? 'line-through' : 'none' }}>
                                           {isMissionary 
                                              ? (isLinked ? `Complete info for ${linkedName}` : "Select or Create Missionary Profile") 
                                              : isGenerational
                                                ? "Complete Generational Form"
                                              : "Complete Family Tree Info"}
                                       </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {isInfoComplete 
                                              ? "Completed" 
                                              : (isMissionary && !isLinked)
                                                ? "Choose a missionary for this order"
                                                : isGenerational
                                                    ? "Use the 'Generational Info' tool below"
                                                : `Use the 'Missionary Info' tool below`}
                                        </Typography>
                                   </Box>
                               </Box>
                           </Grid>
                       )}

                       {/* Photo Step */}
                       <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'white', borderRadius: 1, border: isPhotosComplete ? '1px solid #e0e0e0' : '1px solid #ffcc80' }}>
                                   {isPhotosComplete ? (
                                       <CheckCircle color="success" />
                                   ) : (
                                       <RadioButtonUnchecked sx={{ color: 'text.disabled' }} /> 
                                   )}
                                   <Box>
                                       <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: isPhotosComplete ? 'text.secondary' : 'text.primary', textDecoration: isPhotosComplete ? 'line-through' : 'none' }}>
                                           Upload Photos
                                       </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {isPhotosComplete ? "Received" : "Use 'Upload Media' tool below"}
                                        </Typography>
                                   </Box>
                               </Box>
                       </Grid>
                   </Grid>
               </Paper>
           )}

           {/* Tools & Actions Grid */}
           <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Project Tools</Typography>
           <Grid container spacing={3} sx={{ mb: 6 }}>
              {/* Context Aware Tools */}
              {isFamilyTree && (
                 <Grid size={{ xs: 12, sm: 4 }}>
                    <Button 
                      fullWidth variant="outlined" 
                      startIcon={<Edit />} 
                      sx={{ p: 2, justifyContent: 'flex-start' }}
                      onClick={() => safeNavigate(`/account/tree?orderId=${orderId}`)}
                    >
                      Open Tree Editor
                    </Button>
                 </Grid>
              )}
              {isMissionary && (
                 <Grid size={{ xs: 12, sm: 4 }}>
                 <Box sx={{ mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    onClick={() => safeNavigate(`/account/mission?orderId=${orderId}`)}
                    startIcon={order.linkedMissionaryId ? <Edit /> : <Add />}
                    disabled={isLocked}
                  >
                    {order.linkedMissionaryId ? `Edit ${order.linkedMissionaryName || 'Info'}` : "Link Missionary Info"}
                  </Button>
                  
                  {/* Change Missionary Action */}
                  {order.linkedMissionaryId && !isLocked &&
                   (order.status === 'pending' || order.status === 'awaiting-info') ? (
                      <Button 
                        size="small" 
                        color="inherit" 
                        sx={{ mt: 1, textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                        onClick={() => safeNavigate(`/account/mission?orderId=${orderId}&mode=select`)}
                      >
                         Not {order.linkedMissionaryName}? Switch Missionary
                      </Button>
                   ) : null}
                </Box>
                 </Grid>
              )}
              {isGenerational && (
                 <Grid size={{ xs: 12, sm: 4 }}>
                    <Button 
                      fullWidth variant="outlined" 
                      startIcon={<Edit />} 
                      sx={{ p: 2, justifyContent: 'flex-start' }}
                      onClick={() => safeNavigate(`/account/generational?orderId=${orderId}`)}
                    >
                      {order.formStatus?.Generational ? "Edit Generational Info" : "Complete Generational Info"}
                    </Button>
                 </Grid>
              )}
              
              {/* Global Tools */}
              <Grid size={{ xs: 12, sm: 4 }}>
                 <Button 
                   fullWidth variant="outlined" 
                   startIcon={<CloudUpload />} 
                   sx={{ p: 2, justifyContent: 'flex-start' }}
                   onClick={() => safeNavigate(`/account/media?orderId=${orderId}`)}
                 >
                   Upload Media/Photos
                 </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                 <Button 
                   fullWidth variant="outlined" 
                   startIcon={<SupportAgent />} 
                   sx={{ p: 2, justifyContent: 'flex-start' }}
                   href="/contact-us"
                 >
                   Contact Artist
                 </Button>
              </Grid>
           </Grid>

           {/* Shipping & Order Items */}
           <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                 <Typography variant="h6" gutterBottom>items</Typography>
                 {order.items.map((item, i) => (
                    <Box key={i} sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                       <Typography>{item.quantity}x {item.name}</Typography>
                       <Typography fontWeight="bold">{item.price}</Typography>
                    </Box>
                 ))}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                 <Typography variant="h6" gutterBottom>Shipping To</Typography>
                 {order.shippingAddress ? (
                    <Box>
                      <Typography>{order.shippingAddress.city}, {order.shippingAddress.state}</Typography>
                      <Typography>{order.shippingAddress.country}</Typography>
                    </Box>
                 ) : (
                    <Typography color="text.secondary">Digital Delivery / No address provided</Typography>
                 )}
              </Grid>
           </Grid>

        </Paper>
      </Container>
    </Box>
  );
};

export default OrderDetailsStudio;
